import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { ApiError } from "../../../../../api";
import useErrorAlert from "../../../../../hooks/useErrorAlert";
import { login } from "../../../../../services/auth";
import getGenericErrorInfo from "../../../../../utils/getGenericErrorInfo";
import { hasProp } from "../../../../../utils/hasProp";

const loginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
  password: Yup.string().required("Required"),
});

type LoginValues = { email: string; password: string };

export default function useLoginScreen({
  onLogin,
}: {
  onLogin: (email: string) => void;
}) {
  const location = useLocation();

  const email = hasProp(location.state, "email")
    ? (location.state.email as string)
    : undefined;

  const navigate = useNavigate();
  const { setError } = useErrorAlert();

  useEffect(() => {
    if (!email) {
      navigate("../email");
    }
  }, [navigate, email]);

  const onSubmit = async (
    values: LoginValues,
    { setFieldError }: { setFieldError: (field: string, error: string) => void }
  ) => {
    try {
      await login(values);
      onLogin(values.email);
    } catch (error) {
      if (
        error instanceof ApiError &&
        // FIXME: oauth errors have a different shape. Should add a guard for these
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (error.info as any)?.error === "invalid_grant"
      ) {
        setFieldError("password", "Incorrect password");
      } else {
        setError(getGenericErrorInfo(error));
      }
    }
  };

  return {
    initialValues: { email: email || "", password: "" },
    onSubmit,
    schema: loginSchema,
    email,
  };
}
